import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

// A lightweight floating CTA that nudges users to scroll to the API Promotion section
// - Observes visibility of the target section (by id)
// - Only shows when the section is not in view
// - Smooth-scrolls to the section on click (respects prefers-reduced-motion)
// - Repositions on mobile to avoid overlapping the mobile Customize button
export default function NudgeToApiPromotion({
  targetId = 'api-promotion',
  label = 'Try our powerful API',
  delayMs = 800,
  threshold = 0.2,
  gradient,
  shadowColors,
}) {
  const [visible, setVisible] = useState(false)
  const [sectionInView, setSectionInView] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [sectionAbove, setSectionAbove] = useState(false) // true if section is scrolled past (above viewport)
  const [sectionBelow, setSectionBelow] = useState(false) // true if section is below viewport

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024) // match lg breakpoint
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const el = document.getElementById(targetId)
    if (!el) return

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        setSectionInView(entry.isIntersecting)
      },
      { root: null, threshold }
    )

    const updatePosition = () => {
      const rect = el.getBoundingClientRect()
      const above = rect.bottom < 0
      const below = rect.top > window.innerHeight
      setSectionAbove(above)
      setSectionBelow(below)
    }

    observer.observe(el)
    updatePosition()
    window.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [targetId, threshold])

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(!sectionInView)
    }, delayMs)
    return () => clearTimeout(t)
  }, [sectionInView, delayMs])

  const handleClick = () => {
    const el = document.getElementById(targetId)
    if (!el) return

    const prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    el.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' })
  }

  if (!visible) return null

  const safeBottom = `calc(24px + env(safe-area-inset-bottom, 0px))`

  const defaultGradient = 'linear-gradient(135deg, #6366F1 0%, #22C55E 50%, #06B6D4 100%)'
  const defaultShadowColors = ['rgba(99,102,241,0.35)', 'rgba(34,197,94,0.35)'] // indigo, green
  const usedGradient = gradient || defaultGradient
  const usedShadowColors = shadowColors && shadowColors.length >= 2 ? shadowColors : defaultShadowColors

  const baseStyle = {
    position: 'fixed',
    zIndex: 1000,
    padding: '0 16px',
    minHeight: '48px', // align with mobile Customize button (~48px)
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.3)',
    background: usedGradient,
    color: '#fff',
    boxShadow: `0 8px 22px ${usedShadowColors[0]}, 0 2px 6px rgba(0,0,0,0.12)`,
    cursor: 'pointer',
    opacity: 0.98,
    backdropFilter: 'saturate(140%) blur(2px)',
    WebkitBackdropFilter: 'saturate(140%) blur(2px)'
  }

  const positionStyle = isMobile
    ? { left: 16, right: 'auto', bottom: safeBottom }
    : { right: 16, bottom: 24 }

  const arrow = sectionAbove ? '↑' : '↓'
  const aria = sectionAbove ? `${label} (scroll up)` : `${label} (scroll down)`

  return (
    <motion.button
      onClick={handleClick}
      aria-label={aria}
      style={{ ...baseStyle, ...positionStyle }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      animate={{
        scale: [1, 1.03, 1],
        boxShadow: [
          `0 8px 22px ${usedShadowColors[0]}, 0 2px 6px rgba(0,0,0,0.12)`,
          `0 10px 26px ${usedShadowColors[1]}, 0 2px 6px rgba(0,0,0,0.12)`,
          `0 8px 22px ${usedShadowColors[0]}, 0 2px 6px rgba(0,0,0,0.12)`
        ],
      }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    >
      <span style={{ fontWeight: 700 }}>{label}</span>
      <span style={{ marginLeft: 8, fontWeight: 700 }}>{arrow}</span>
    </motion.button>
  )
}

